import {
  LinkedCollection,
  MegaMenu,
  MegamenuDocumentDataCollectionsItem,
  MegamenuDocumentDataHighlightedLinksItem,
  MegaMenuEntry,
  MegamenuentryDocument,
  PrismicCollectionIntegrationField,
} from '@monofront/types';
import {
  ITagContextMatchesParams,
  tagContextMatches,
} from './tagContextMatches';

export const getMegamenuEntryLabel = (entry: {
  labelOverride: string | null;
  linkedCollection: LinkedCollection;
}) => entry.labelOverride || entry.linkedCollection.title;

const buildCollectionsTree = (
  collections: MegamenuDocumentDataCollectionsItem[],
  megamenuEntries: MegamenuentryDocument[],
  conditionsContext: ITagContextMatchesParams,
  ancestorCollections: Set<string>,
): MegaMenuEntry[] => {
  return collections.reduce((acc, { reference }) => {
    const associatedEntry = megamenuEntries.find(
      (entry) =>
        reference.link_type === 'Document' && entry.uid === reference.uid,
    );

    if (!associatedEntry) {
      // filtering incorrect entry
      return acc;
    }

    const {
      data: {
        linked_collection,
        label_override,
        merch_image,
        merch_link,
        subcollections,
        condition,
      },
    } = associatedEntry;

    const collectionExistsUpperInTheBranch = ancestorCollections.has(
      associatedEntry.uid,
    );

    if (
      !tagContextMatches(condition ?? '', conditionsContext) ||
      !linked_collection ||
      collectionExistsUpperInTheBranch
    ) {
      // filtering entry that should not be displayed
      return acc;
    }

    const linkedCollection =
      linked_collection as PrismicCollectionIntegrationField;

    const megamenuEntry: MegaMenuEntry = {
      linkedCollection: {
        handle: linkedCollection.handle,
        title: linkedCollection.title,
        icon: linkedCollection.icon,
        id: linkedCollection.id,
      },
      labelOverride: label_override,
      merchImage: merch_image,
      merchLink: merch_link,
      subCollections: buildCollectionsTree(
        subcollections,
        megamenuEntries,
        conditionsContext,
        // keep track of collections in branch to avoid circular references
        new Set([...ancestorCollections, associatedEntry.uid]),
      ),
    };

    return [...acc, megamenuEntry];
  }, [] as MegaMenuEntry[]);
};

export const buildMegamenu = (
  highlightedLinks: MegamenuDocumentDataHighlightedLinksItem[],
  collections: MegamenuDocumentDataCollectionsItem[],
  megamenuEntries: MegamenuentryDocument[],
  contextMatchingVariables: ITagContextMatchesParams,
): MegaMenu => {
  const mappedHighlightedLinks = highlightedLinks
    .filter(
      (link) =>
        tagContextMatches(link.condition ?? '', contextMatchingVariables) &&
        link.linked_collection,
    )
    .map((link) => {
      const linkedCollection =
        link.linked_collection as PrismicCollectionIntegrationField;

      return {
        linkedCollection: {
          handle: linkedCollection.handle,
          title: linkedCollection.title,
          icon: linkedCollection.icon,
          id: linkedCollection.id,
        },
        labelOverride: link.label_override,
        labelColor: link.color,
        labelBackgroundColor: link.background_color,
        labelTag: link.tag_label,
      };
    });

  const mappedCollections = buildCollectionsTree(
    collections,
    megamenuEntries,
    contextMatchingVariables,
    new Set(),
  );

  return {
    highlightedLinks: mappedHighlightedLinks,
    collections: mappedCollections,
  };
};

export const buildMegamenuEntries = (
  megamenu: MegaMenu,
  isMobile: boolean,
): MegaMenuEntry[] => {
  if (!isMobile) {
    // on desktop we display only collections in megamenu
    return megamenu.collections;
  }

  const mappedHighlightedLinks: MegaMenuEntry[] = megamenu.highlightedLinks.map(
    (link) => ({
      labelOverride: link.labelOverride,
      linkedCollection: link.linkedCollection,
      merchImage: {}, // useless on mobile
      merchLink: { link_type: 'Any' }, // useless on mobile
      subCollections: [],
    }),
  );

  // on mobile we add highlighted links on top of the list (the one that are outside on desktop)
  return [...mappedHighlightedLinks, ...megamenu.collections];
};
